import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {useEffect, useMemo, useRef} from 'react';
import {useAuth} from '@api/auth';
import {useActiveWorkspace, useMyAccess} from '@api/workspaces';
import {IS_MOCK} from '@api/mock-mode';
import {analyticsInsightsClient} from './client';
import {isAnalyticsInsightStateConflict} from './errors';
import type {
  AnalyticsInsightInclude,
  AnalyticsInsightMyStatePatch,
  AnalyticsInsightsRequest,
  CreateAnalyticsInsightTaskInput,
} from './types';

/** Персональные ключи: actor и workspace из текущего auth-контекста. */
export const analyticsInsightKeys = {
  all: ['analytics-insights'] as const,
  workspace: (workspaceSid: string | null, actorSid: string | null) =>
    ['analytics-insights', actorSid, workspaceSid] as const,
  lists: (workspaceSid: string | null, actorSid: string | null) =>
    ['analytics-insights', actorSid, workspaceSid, 'list'] as const,
  list: (
    workspaceSid: string | null,
    actorSid: string | null,
    request: AnalyticsInsightsRequest,
  ) => ['analytics-insights', actorSid, workspaceSid, 'list', request] as const,
  detail: (
    workspaceSid: string | null,
    actorSid: string | null,
    insightSid: string | null,
    include: AnalyticsInsightInclude[],
  ) =>
    [
      'analytics-insights',
      actorSid,
      workspaceSid,
      'detail',
      insightSid,
      include,
    ] as const,
};

const useInsightIdentity = () => {
  const {activeId} = useActiveWorkspace();
  const {currentUser} = useAuth();
  const access = useMyAccess(activeId);
  const accessSignature = JSON.stringify(access.data ?? null);
  const previousAccess = useRef(accessSignature);
  const actorSid = currentUser?.sid ?? (IS_MOCK ? 'mock-user' : null);
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] === 'analytics-insights' &&
        (query.queryKey[1] !== actorSid || query.queryKey[2] !== activeId),
    });
  }, [actorSid, activeId, queryClient]);
  useEffect(() => {
    if (previousAccess.current !== accessSignature) {
      previousAccess.current = accessSignature;
      void queryClient.resetQueries({
        queryKey: analyticsInsightKeys.workspace(activeId, actorSid),
      });
    }
  }, [accessSignature, activeId, actorSid, queryClient]);
  return {activeId, actorSid};
};

/** Cursor-пагинация; пустая страница с cursor сохраняет возможность загрузить ещё. */
export const useAnalyticsInsights = (
  request: Omit<AnalyticsInsightsRequest, 'cursor'>,
  options: {enabled?: boolean} = {},
) => {
  const {activeId, actorSid} = useInsightIdentity();
  const queryClient = useQueryClient();
  const queryKey = analyticsInsightKeys.list(activeId, actorSid, request);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({pageParam}) =>
      analyticsInsightsClient.list(activeId ?? 'mock-workspace', {
        ...request,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.cursor ?? undefined,
    enabled:
      (IS_MOCK || Boolean(activeId)) &&
      Boolean(actorSid) &&
      (options.enabled ?? true),
    staleTime: 30_000,
    gcTime: 0,
  });
  const rows = useMemo(
    () =>
      query.isError
        ? []
        : (query.data?.pages.flatMap((page) => page.rows) ?? []),
    [query.data, query.isError],
  );
  const refresh = () => queryClient.resetQueries({queryKey, exact: true});
  return {...query, rows, refresh};
};

/** Детальная карточка со своим состоянием; открытие не выполняет PATCH. */
export const useAnalyticsInsight = (
  insightSid: string | null,
  include: AnalyticsInsightInclude[] = [
    'enrichment',
    'tasks',
    'run',
    'my-state',
  ],
) => {
  const {activeId, actorSid} = useInsightIdentity();
  return useQuery({
    queryKey: analyticsInsightKeys.detail(
      activeId,
      actorSid,
      insightSid,
      include,
    ),
    queryFn: () =>
      analyticsInsightsClient.get(
        activeId ?? 'mock-workspace',
        insightSid as string,
        include,
      ),
    enabled:
      (IS_MOCK || Boolean(activeId)) &&
      Boolean(actorSid) &&
      Boolean(insightSid),
    gcTime: 0,
  });
};

/** Сохранение собственной отметки: без оптимистического скрытия и слепого retry. */
export const useUpdateAnalyticsInsightMyState = () => {
  const {activeId, actorSid} = useInsightIdentity();
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all([
      queryClient.resetQueries({
        queryKey: analyticsInsightKeys.lists(activeId, actorSid),
      }),
      queryClient.invalidateQueries({
        queryKey: [
          ...analyticsInsightKeys.workspace(activeId, actorSid),
          'detail',
        ],
      }),
    ]);
  return useMutation({
    mutationFn: ({
      insightSid,
      dto,
    }: {
      insightSid: string;
      dto: AnalyticsInsightMyStatePatch;
    }) =>
      analyticsInsightsClient.updateMyState(
        activeId ?? 'mock-workspace',
        insightSid,
        dto,
      ),
    retry: false,
    onSuccess: refresh,
    onError: async (error) => {
      if (isAnalyticsInsightStateConflict(error)) await refresh();
    },
  });
};

/** Создать Task: личная отметка меняется только отдельным явным действием. */
export const useCreateAnalyticsInsightTask = () => {
  const {activeId, actorSid} = useInsightIdentity();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      insightSid,
      input,
      idempotencyKey,
    }: {
      insightSid: string;
      input: CreateAnalyticsInsightTaskInput;
      idempotencyKey: string;
    }) =>
      analyticsInsightsClient.createTask(
        activeId ?? 'mock-workspace',
        insightSid,
        input,
        idempotencyKey,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: analyticsInsightKeys.workspace(activeId, actorSid),
      });
      void queryClient.invalidateQueries({queryKey: ['tasks']});
    },
  });
};
